import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import productData from "../Datas/productData";

export const CartContext = createContext();

const CART_STORAGE_KEY = "cartItems";
const WISHLIST_STORAGE_KEY = "wishlistItems";
const ORDER_NOTE_STORAGE_KEY = "cartOrderNote";
const MAX_QUANTITY = 99;
const MAX_ORDER_NOTE_LENGTH = 300;
const catalogProductIds = new Set(productData.map((product) => product.id));

const normalizeProductId = (productId) => {
  const parsedId = Number(productId);
  return Number.isInteger(parsedId) && catalogProductIds.has(parsedId)
    ? parsedId
    : null;
};

const normalizeQuantity = (quantity, fallback = 1) => {
  const parsedQuantity = Number(quantity);

  if (!Number.isFinite(parsedQuantity)) {
    return fallback;
  }

  return Math.min(MAX_QUANTITY, Math.max(1, Math.trunc(parsedQuantity)));
};

const normalizeOption = (value) => String(value ?? "").trim();

const sanitizeCartItems = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  const cartByVariant = new Map();

  value.forEach((item) => {
    const id = normalizeProductId(item?.id);

    if (id === null) {
      return;
    }

    const size = normalizeOption(item?.size);
    const color = normalizeOption(item?.color);
    const quantity = normalizeQuantity(item?.quantity);
    const variantKey = `${id}::${size}::${color}`;
    const existingItem = cartByVariant.get(variantKey);

    cartByVariant.set(variantKey, {
      id,
      size,
      color,
      quantity: normalizeQuantity((existingItem?.quantity ?? 0) + quantity),
    });
  });

  return Array.from(cartByVariant.values());
};

const sanitizeWishlistItems = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return Array.from(
    new Set(
      value
        .map((productId) => normalizeProductId(productId))
        .filter((productId) => productId !== null)
    )
  );
};

const sanitizeOrderNote = (value) =>
  typeof value === "string" ? value.slice(0, MAX_ORDER_NOTE_LENGTH) : "";

const readStoredItems = (storageKey, sanitizer, fallback = []) => {
  if (typeof window === "undefined") {
    return fallback;
  }

  try {
    const storedValue = window.localStorage.getItem(storageKey);
    return storedValue ? sanitizer(JSON.parse(storedValue)) : fallback;
  } catch {
    return fallback;
  }
};

const persistItems = (storageKey, items) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(items));
  } catch {
    // Storage can be unavailable in private browsing or restricted environments.
  }
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    readStoredItems(CART_STORAGE_KEY, sanitizeCartItems)
  );
  const [wishlistItems, setWishlistItems] = useState(() =>
    readStoredItems(WISHLIST_STORAGE_KEY, sanitizeWishlistItems)
  );
  const [orderNote, setOrderNote] = useState(() =>
    readStoredItems(ORDER_NOTE_STORAGE_KEY, sanitizeOrderNote, "")
  );

  useEffect(() => {
    persistItems(CART_STORAGE_KEY, cartItems);
  }, [cartItems]);

  useEffect(() => {
    persistItems(WISHLIST_STORAGE_KEY, wishlistItems);
  }, [wishlistItems]);

  useEffect(() => {
    persistItems(ORDER_NOTE_STORAGE_KEY, orderNote);
  }, [orderNote]);

  const addToCart = useCallback(
    (productId, quantity = 1, size = "", color = "") => {
      const id = normalizeProductId(productId);

      if (id === null) {
        return;
      }

      const normalizedQuantity = normalizeQuantity(quantity);
      const normalizedSize = normalizeOption(size);
      const normalizedColor = normalizeOption(color);

      setCartItems((previousItems) => {
        const existingItem = previousItems.find(
          (item) =>
            item.id === id &&
            item.size === normalizedSize &&
            item.color === normalizedColor
        );

        if (!existingItem) {
          return [
            ...previousItems,
            {
              id,
              size: normalizedSize,
              color: normalizedColor,
              quantity: normalizedQuantity,
            },
          ];
        }

        return previousItems.map((item) =>
          item.id === id &&
          item.size === normalizedSize &&
          item.color === normalizedColor
            ? {
                ...item,
                quantity: normalizeQuantity(
                  item.quantity + normalizedQuantity
                ),
              }
            : item
        );
      });
    },
    []
  );

  const removeFromCart = useCallback(
    (productId, size = "", color = "") => {
      const id = normalizeProductId(productId);

      if (id === null) {
        return;
      }

      const normalizedSize = normalizeOption(size);
      const normalizedColor = normalizeOption(color);

      setCartItems((previousItems) =>
        previousItems.filter(
          (item) =>
            !(
              item.id === id &&
              item.size === normalizedSize &&
              item.color === normalizedColor
            )
        )
      );
    },
    []
  );

  const updateQuantity = useCallback(
    (productId, size = "", color = "", quantity = 1) => {
      const id = normalizeProductId(productId);

      if (id === null) {
        return;
      }

      const normalizedSize = normalizeOption(size);
      const normalizedColor = normalizeOption(color);
      const normalizedQuantity = normalizeQuantity(quantity);

      setCartItems((previousItems) =>
        previousItems.map((item) =>
          item.id === id &&
          item.size === normalizedSize &&
          item.color === normalizedColor
            ? { ...item, quantity: normalizedQuantity }
            : item
        )
      );
    },
    []
  );

  const clearCart = useCallback(() => {
    setCartItems([]);
    setOrderNote("");
  }, []);

  const updateOrderNote = useCallback((value) => {
    setOrderNote(sanitizeOrderNote(value));
  }, []);

  const addToWishlist = useCallback((productId) => {
    const id = normalizeProductId(productId);

    if (id === null) {
      return;
    }

    setWishlistItems((previousItems) =>
      previousItems.includes(id) ? previousItems : [...previousItems, id]
    );
  }, []);

  const removeFromWishlist = useCallback((productId) => {
    const id = normalizeProductId(productId);

    if (id === null) {
      return;
    }

    setWishlistItems((previousItems) =>
      previousItems.filter((itemId) => itemId !== id)
    );
  }, []);

  const toggleWishlist = useCallback((productId) => {
    const id = normalizeProductId(productId);

    if (id === null) {
      return;
    }

    setWishlistItems((previousItems) =>
      previousItems.includes(id)
        ? previousItems.filter((itemId) => itemId !== id)
        : [...previousItems, id]
    );
  }, []);

  const isInWishlist = useCallback(
    (productId) => {
      const id = normalizeProductId(productId);
      return id !== null && wishlistItems.includes(id);
    },
    [wishlistItems]
  );

  const contextValue = useMemo(
    () => ({
      cartItems,
      wishlistItems,
      orderNote,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      updateOrderNote,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
      isInWishlist,
    }),
    [
      addToCart,
      addToWishlist,
      cartItems,
      clearCart,
      isInWishlist,
      orderNote,
      removeFromCart,
      removeFromWishlist,
      toggleWishlist,
      updateQuantity,
      updateOrderNote,
      wishlistItems,
    ]
  );

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};
